import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { fmt } from "../lib/calc";
import { StatusChip } from "../components/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const Row = ({ it, onDelete }) => (
  <li className="hist-item" data-testid={`history-item-${it.id}`}>
    <div className="hist-top">
      <StatusChip status={it.status} testId={`history-status-${it.id}`} />
      <span className="hist-date">{new Date(it.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
      <button className="icon-btn" data-testid={`btn-delete-history-${it.id}`} onClick={() => onDelete(it)} aria-label="Delete"><Trash2 size={16} /></button>
    </div>
    <div className="hist-label" data-testid={`history-label-${it.id}`}>{it.label || "Untitled load"}</div>
    <div className="hist-nums">
      <div><span>True cost</span><b>{fmt.money(it.true_trip_cost)}</b></div>
      <div><span>Rec. quote</span><b>{fmt.money(it.recommended_quote)}</b></div>
      <div><span>Profit</span><b className={it.projected_profit >= 0 ? "pos" : "neg"}>{fmt.money(it.projected_profit)}</b></div>
    </div>
  </li>
);

export default function HistoryPage() {
  const [items, setItems] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/results").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  }, []);

  const confirmDelete = async () => {
    const it = pending;
    setPending(null);
    try {
      await api.delete(`/results/${it.id}`);
      setItems((xs) => xs.filter((x) => x.id !== it.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="narrow" data-testid="history-page">
      <h1 className="page-title">History</h1>
      <p className="page-sub">{items ? `${items.length} saved result${items.length === 1 ? "" : "s"}` : "Loading…"}</p>
      {items && items.length === 0 && (
        <div className="empty" data-testid="history-empty">
          <Inbox size={32} />
          <p>No saved loads yet.</p>
          <Link to="/" className="btn primary sm" data-testid="history-go-calculator">Run a calculation</Link>
        </div>
      )}
      <ul className="hist-list" data-testid="history-list">
        {items?.map((it) => <Row key={it.id} it={it} onDelete={setPending} />)}
      </ul>
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="dlg" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this result?</AlertDialogTitle>
            <AlertDialogDescription>{pending?.label || "Untitled load"} will be removed permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" className="dlg-danger" onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
